/**
 * Типы OKDoc tracking описывают ссылки, события и summary админки.
 * Дата: 16.03.2026
 */

import type {
  OkdocCTAActionKind,
  OkdocInteractionIntent,
  OkdocScenarioFamily,
  OkdocScenarioKind,
  OkdocScenarioProfile,
  OkdocSourceContext,
} from '@/features/okdoc/types';

export type OkdocFlow = 'appointment_confirmation' | 'feedback_collection' | 'external_redirect' | 'recommendations';

export type OkdocLinkEventType =
  | 'landing_open'
  | 'landing_reopen'
  | 'cta_click'
  | 'redirect_click'
  | 'review_click'
  | 'submit_attempt'
  | 'submit_success'
  | 'submit_rejected_used'
  | 'submit_rejected_expired';

export interface OkdocTrackingLink {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  tenant_slug?: string;
  code: string;
  phone: string;
  patient_name?: string;
  appointment_id?: string;
  template_id?: string;
  message_id?: string;
  flow: OkdocFlow;
  scenario_kind?: OkdocScenarioKind | string;
  scenario_family?: OkdocScenarioFamily | string;
  source_context?: OkdocSourceContext | string;
  scenario_profile?: OkdocScenarioProfile;
  event_type: string;
  source_channel: string;
  expires_at: string;
  used_at?: string;
  used_action?: string;
  first_opened_at?: string;
  last_opened_at?: string;
  open_count: number;
  last_event_at?: string;
  last_event_type?: OkdocLinkEventType;
  last_ip?: string;
  last_user_agent?: string;
  first_submitted_at?: string;
  submit_count: number;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface OkdocTrackingEvent {
  id: string;
  link_id: string;
  tenant_id: string;
  code: string;
  flow: OkdocFlow;
  event_type: OkdocLinkEventType;
  appointment_id?: string;
  template_id?: string;
  message_id?: string;
  patient_phone: string;
  patient_name?: string;
  ip_address?: string;
  user_agent?: string;
  referer?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface OkdocTrackingSummary {
  total_links: number;
  opened_links: number;
  landing_opens: number;
  cta_clicks: number;
  redirect_clicks: number;
  review_clicks: number;
  submit_attempts: number;
  submit_success: number;
  conversion_rate: number;
}

export interface OkdocTrackingListResponse {
  links: OkdocTrackingLink[];
  total: number;
  limit: number;
  offset: number;
  summary: OkdocTrackingSummary;
}

export interface OkdocTrackingDetailsResponse {
  link: OkdocTrackingLink;
  events: OkdocTrackingEvent[];
  total_events: number;
}

export interface OkdocTrackingFilters {
  tenant_id?: string;
  search?: string;
  flow?: OkdocFlow | '';
  event_type?: OkdocLinkEventType | '';
  scenario_kind?: OkdocScenarioKind | '' | string;
  scenario_family?: OkdocScenarioFamily | '' | string;
  source_context?: OkdocSourceContext | '' | string;
  cta_action_kind?: OkdocCTAActionKind | '' | string;
  interaction_intent?: OkdocInteractionIntent | '' | string;
  template_id?: string;
  appointment_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export const OKDOC_FLOW_LABELS: Record<OkdocFlow, string> = {
  appointment_confirmation: 'Подтверждение записи',
  feedback_collection: 'Сбор ОС',
  external_redirect: 'Внешний переход',
  recommendations: 'Рекомендации',
};

export const OKDOC_EVENT_LABELS: Record<OkdocLinkEventType, string> = {
  landing_open: 'Открытие ссылки',
  landing_reopen: 'Повторное открытие',
  cta_click: 'Нажатие CTA',
  redirect_click: 'Переход по ссылке',
  review_click: 'Переход к отзыву',
  submit_attempt: 'Попытка отправки',
  submit_success: 'Ответ зафиксирован',
  submit_rejected_used: 'Ссылка уже использована',
  submit_rejected_expired: 'Ссылка просрочена',
};
